import { hexToBytes } from "../crypto/envelope";
import {
  registerAuthSlice,
  resetTableSlice,
  useRootStore,
  type SliceSet,
} from "../store/root";
import { useUIStore } from "../store/ui";
import { publishCrossTab } from "../session/cross_tab";
import {
  clearPersistedSession,
  persistSession,
  type RestoredSession,
} from "./signing_key_idb";

/**
 * Auth slice — captures the logged-in account's signing key + WS-
 * table binding so the action-bar wiring can sign and POST envelopes
 * without rederiving on every click. Composed into the unified root
 * store (`store/root.ts`) as one of two slices; the auth fields and
 * the game fields commit through the same atomic `set`, but `login()`
 * / `logout()` only ever touch the auth-slice fields (the game
 * slice's own `reset()` clears the table state), so the signing key
 * never persists across a game-state teardown.
 *
 * Signing key lifetime (cryptographer lock-in): the seed is `null` until
 * `login()` populates it and stays in memory for the rest of the
 * session. `logout()` wipes the in-place buffer + clears the slot
 * — best effort given JS GC. Page unload empties the heap entirely.
 *
 * Server behavior carry: the current backend's `/accounts/login`
 * returns the 32-byte ed25519 signing key in the response body. The
 * client-side Argon2id derivation from `src/crypto/argon2.ts` is
 * future infrastructure — when the server moves to a verifying-
 * key-only model the same auth slice swaps the `signingKey` source
 * from "wire response" to "Argon2id derive" without touching the
 * UI layer.
 *
 * ## Session state machine (single source of truth)
 *
 * The session lifecycle is modelled as ONE typed discriminated union
 * (`sessionState`) that names every legal state and makes the
 * invalid cross-products structurally unrepresentable. The legacy
 * flat fields (`phase` / `bootProbePending` / `walletKnown` /
 * `mode` / `mySeat` / `signingKey` / `csrfToken` / identity / wallet
 * mirrors) are NOT independent slots anymore — they are pure
 * derivations recomputed from `sessionState` inside the same atomic
 * Zustand `set` on every transition. Consumers keep reading the flat
 * fields unchanged; `sessionState` is the sole writer. This kills the
 * representable-but-invalid combos the old four-slot model allowed:
 * `walletKnown=true while probing`, `bootProbePending while table /
 * logged_out`, `tableIdHex set while logged out`, `key present while
 * logged out` (and vice versa), `mode=spectator with mySeat set`.
 */

/**
 * Caller's table-membership lane. `'player'` is the seated lane
 * (signed envelopes, `mySeat ≠ null`); `'spectator'` is the read-only
 * lane (mid-hand auto-spectate or manual 관전 모드 — no seat). This is
 * a DERIVED flat mirror only — the lane is no longer a stored field
 * on the membership; the session-state KIND (`seated` vs `spectating`)
 * IS the lane, and `deriveLegacyFields` projects it back to this enum.
 * The mode drives both the WS lane query parameter and the
 * defense-in-depth strips in the game store reducer + selectors so a
 * server-side rewrite miss cannot leak `final_seed` through to a
 * spectator-side derive path.
 */
export type AuthMode = "player" | "spectator";

/**
 * Account-session lifecycle phase, aligned to the standard
 * single-active-surface multi-session model. The AuthGate reads
 * this enum directly so the modal overlay + LoginScreen swap is
 * unambiguous. It is now a pure derivation of `sessionState`:
 *
 *   `logged_out` / `restoring`  → `"logged_out"` (LoginScreen)
 *   `probing` / `lobby`         → `"lobby"`
 *   `seated` / `spectating`     → `"table"`
 *
 * so the historical invariant
 *
 *   `phase === "logged_out"` ⟺ `signingKey === null`   ⟺ `tableIdHex === null`
 *   `phase === "lobby"`      ⟺ `signingKey !== null`  ⟺ `tableIdHex === null`
 *   `phase === "table"`      ⟺ `signingKey !== null`  ⟺ `tableIdHex !== null`
 *
 * still holds by construction (the credential bundle lives only in
 * `probing`/`lobby`/`seated`/`spectating`; the table id lives only in
 * `seated`/`spectating`). It cannot drift between two consumer reads
 * because every transition recomputes the derived mirrors inside a
 * single Zustand `set`.
 */
export type AuthPhase = "logged_out" | "lobby" | "table";

/**
 * The credential bundle present in every AUTHENTICATED session
 * state (`probing` / `lobby` / `seated` / `spectating`) and ABSENT —
 * hence unrepresentable — in `logged_out` / `restoring`. Carrying the
 * credentials inside the discriminant is what makes
 * "logged in without a signing key" and "logged out with a key
 * present" both structurally impossible.
 */
export interface AuthIdentity {
  readonly username: string;
  readonly accountId: number;
  readonly playerIdHex: string;
  /** 32-byte signing key (ed25519 seed). */
  readonly signingKey: Uint8Array;
  /** Genesis/rotated ed25519 credential epoch (0 at genesis). */
  readonly ed25519Epoch: number;
  /** Hybrid AND-mode second leg — the 32-byte ML-DSA-65 SEED (NOT
   *  the ~4032B secretKey). `null` for a non-hybrid account. */
  readonly mlDsaSeed: Uint8Array | null;
  /** ml_dsa credential epoch hint. `null` until a hybrid credential
   *  is present. */
  readonly mlDsaEpoch: number | null;
  /** CSRF Double-Submit / Synchronizer Token (ADR-010). */
  readonly csrfToken: string;
  /** Backend-issued account locale. `null` when the row never set a
   *  preference. */
  readonly locale: string | null;
}

/**
 * The SEATED-lane table membership payload. `mySeat` is NON-NULL by
 * type — a seated player ALWAYS occupies a seat. "Seated with a null
 * seat" is now structurally unrepresentable (it was only excluded by
 * convention while the membership carried a nullable `mySeat`).
 */
export interface SeatedTable {
  readonly tableIdHex: string;
  readonly mySeat: number;
}

/**
 * The SPECTATING-lane table membership payload. It carries NO
 * `mySeat` field AT ALL — a spectator has no seat, so "spectating
 * with a seat" cannot be expressed. The variant KIND (`spectating`)
 * IS the lane; there is no `lane` field to drift out of sync with
 * the presence/absence of a seat.
 */
export interface SpectatingTable {
  readonly tableIdHex: string;
}

/**
 * The session lifecycle as a single discriminated union — the SOLE
 * source of truth. Every legacy flat field is derived from this.
 *
 * - `logged_out`  — brand-new tab / post-logout. No credentials.
 * - `restoring`   — boot IDB decrypt in flight; creds not yet in the
 *                   slice. Mostly sub-frame (the decrypt races
 *                   `createRoot`); modelled so the boot window is
 *                   NAMED instead of masquerading as `logged_out`.
 *                   Derives `phase === "logged_out"` so the surface
 *                   is byte-identical to today.
 * - `probing`     — creds restored, the boot session-validity probe
 *                   is in flight. Has NO `walletKnown` field — the
 *                   wallet is structurally unknown during a probe.
 *                   Derives `phase === "lobby"` + `bootProbePending`
 *                   so the AuthGate holds the lobby behind the
 *                   neutral fallback, exactly as before.
 * - `lobby`       — authenticated, not at a table. `walletKnown`
 *                   distinguishes "wallet genuinely zero" from
 *                   "wallet not yet fetched" (post-restore transient
 *                   `false`).
 * - `seated`      — authenticated + a SEATED table membership
 *                   (`mySeat` non-null by type). The player lane.
 * - `spectating`  — authenticated + a SPECTATING table membership (no
 *                   seat field). The read-only lane. Spectator-ness is
 *                   now the variant KIND, not a `lane` flag carried
 *                   beside a by-convention-null seat: "seated with no
 *                   seat" and "spectating with a seat" are both
 *                   unrepresentable.
 */
export type SessionState =
  | { kind: "logged_out" }
  | { kind: "restoring" }
  | { kind: "probing"; account: AuthIdentity }
  | { kind: "lobby"; account: AuthIdentity; walletKnown: boolean }
  | {
      kind: "seated";
      account: AuthIdentity;
      walletKnown: boolean;
      table: SeatedTable;
    }
  | {
      kind: "spectating";
      account: AuthIdentity;
      walletKnown: boolean;
      table: SpectatingTable;
    };

export interface AuthState {
  /** Session lifecycle — the SOLE source of truth. Every field below
   *  is derived from this inside the same atomic `set`; consumers may
   *  read the derived mirrors OR (in a later stage) narrow on this. */
  sessionState: SessionState;
  /** Lifecycle phase — derived from `sessionState`. See `AuthPhase`. */
  phase: AuthPhase;
  username: string | null;
  accountId: number | null;
  playerIdHex: string | null;
  /** 32-byte signing key (ed25519 seed). `null` until login. */
  signingKey: Uint8Array | null;
  /** Genesis/rotated ed25519 credential epoch (0 at genesis). The signer
   *  keys the auth epoch-trailer + `signing_cred` emission off THIS value
   *  — never off `mlDsaEpoch`. */
  ed25519Epoch: number;
  /** Hybrid AND-mode second leg: the 32-byte ML-DSA-65 SEED (NOT the
   *  ~4032B secretKey, which is re-derived per use via `deriveMlDsa`).
   *  `null` for a non-hybrid (today / un-enrolled) account. Zeroized
   *  alongside `signingKey` on logout. */
  mlDsaSeed: Uint8Array | null;
  /** The ml_dsa credential epoch hint (DB-current). `null` until a hybrid
   *  credential is present. */
  mlDsaEpoch: number | null;
  /** Free off-table chips (= BE `accounts.chip_balance`). Counts
   *  only chips that are NOT locked behind an open table hold;
   *  the rebuy / buy-in / wallet-floor surfaces clamp against
   *  this field because it is the only spendable slice. The
   *  cap-style display composes `chipTotal = chipFree +
   *  chipHoldTotal` per the [[wallet-model-rethink]] converge —
   *  see `src/session/wallet_sync.ts` for the refresh path. */
  chipFree: number;
  /** Sum of active wallet holds — chips locked behind open
   *  table buy-ins across every seated table. `0` until the
   *  account seats somewhere; the `/accounts/me/sync` poll
   *  refreshes this on Lobby mount / table-leave / WS-reconnect
   *  so a reconnecting player sees their "$N 진행 중" line
   *  instead of misreading free-only as "everything lost". */
  /** Chips at tables right now — the sum of live seat stacks. Moves
   *  with every pot, so `chipFree + chipInPlay` is always what the
   *  player is worth. This is what the badge calls "진행 중", and what
   *  every room this one is measured against calls "in play". */
  chipInPlay: number;
  /** The reservation taken out of the wallet to sit down. NOT what is
   *  at the table once a pot has been lost — it does not move while
   *  chips are won and lost. Kept for the orphan-hold surface, which
   *  is about money stuck at a table this client has left. */
  chipHoldTotal: number;
  /** Backend-issued account locale. `null` when the row never set
   *  a preference; the i18n negotiation chain falls through to
   *  localStorage / navigator / ko-KR fallback. Lowercase BCP-47
   *  tag (`"ko-kr"`, `"en"`, `"en-us"`); `normaliseLocale` in
   *  `src/i18n` accepts every
   *  variant + canonicalises to the supported set. */
  locale: string | null;
  /** Currently joined table id in lowercase hex. */
  tableIdHex: string | null;
  /** Seat index the caller occupies at `tableIdHex`. Derived: the
   *  non-null seat of the `seated` variant; `null` everywhere else
   *  (including `spectating`). The seated/spectator paths are now
   *  structurally mutually exclusive — they are SEPARATE session
   *  variants, not one variant with a nullable seat + a lane flag. */
  mySeat: number | null;
  /** Table-membership lane — see `AuthMode` doc. Derived from the
   *  session-state KIND: `'spectator'` iff `kind === 'spectating'`,
   *  else `'player'`. `joinTable()` → `seated` (player),
   *  `joinAsSpectator()` → `spectating` (spectator), `leaveTable()`
   *  → `lobby` (player). */
  mode: AuthMode;
  /** Base URL for the backend. Empty string in production
   *  (same-origin via tower-http ServeDir); `http://127.0.0.1:8080`
   *  in dev when Vite proxies. Orthogonal to the session lifecycle —
   *  it is plumbing, not a session fact, so it lives outside
   *  `sessionState` and survives every transition (set once at boot
   *  via `setServerBase`). */
  serverBase: string;
  /** CSRF Double-Submit / Synchronizer Token (ADR-010).
   *  The BE issues a hex-encoded
   *  token on `POST /accounts/login` (`csrf_token` field of the
   *  response body) bound to the same session row the `Set-Cookie`
   *  header arms. Every mutating authed fetch echoes the value back
   *  via the `x-csrf-token` header; `authedRequest` in
   *  `src/session/authed_request.ts` reads this slot. `null` until
   *  login lands + after `logout()` wipes the session. */
  csrfToken: string | null;
  /** Boot-rehydrate session-validity gate. Derived: `true` iff
   *  `sessionState.kind === "probing"`. `restoreSession()` arms it by
   *  entering `probing` so the AuthGate holds the lobby behind a
   *  neutral loading shell until the boot probe (`main.tsx`) confirms
   *  the restored cookie is still live. A stale session (the BE swept
   *  the idle/absolute-TTL row) then logs out via the probe's
   *  session-class 401 cascade WITHOUT the lobby ever painting its
   *  alarming "예약된 좌석" banner + "$0" wallet. `false` after a
   *  fresh `login()` (no probe needed — the cookie is brand-new) and
   *  after `clearBootProbe()` resolves the probe to `lobby`. */
  bootProbePending: boolean;
  /** `true` once a real wallet snapshot has landed (from the
   *  `login()` response, a `/accounts/me/sync` poll via `setWallet`,
   *  or the boot probe via `clearBootProbe`). `restoreSession()`
   *  leaves it `false` because the restored slice carries the
   *  ZERO placeholders, not a real balance — the Lobby shows
   *  "불러오는 중…" instead of a misleading "$0" until the first
   *  real snapshot resolves. Derived: `false` in
   *  `logged_out`/`restoring`/`probing`; the union's own
   *  `walletKnown` in `lobby`/`seated`/`spectating`. Distinguishes "wallet is
   *  genuinely zero" (known) from "wallet not yet fetched"
   *  (unknown). */
  walletKnown: boolean;
}

export interface AuthActions {
  login(args: {
    username: string;
    accountId: number;
    playerIdHex: string;
    signingKeyHex: string;
    chipFree: number;
    chipInPlay: number;
    chipHoldTotal: number;
    /** Optional account locale (`null` when the account row never
     *  set a preference). Hands the negotiation chain the
     *  highest-precedence input. */
    locale?: string | null;
    /** CSRF Double-Submit token the BE issues alongside the
     *  session cookie (ADR-010). Stored verbatim so every
     *  mutating authed fetch can echo it back. */
    csrfToken: string;
    /** The second signing leg of an account with quantum-safe sign-in
     *  on (`LoginResult.mlDsa`). Absent / `null` for every other. */
    mlDsa?: { seedHex: string; epoch: number } | null;
  }): void;
  /** Rotate the cached CSRF token. The BE is the source of
   *  truth — call this after any future endpoint that re-issues
   *  the token (e.g. session refresh). Atomic with the next
   *  authed-fetch read because Zustand `set` is synchronous.
   *  No-op when there is no authenticated identity to rotate
   *  against (the token lives inside `AuthIdentity`). */
  setCsrfToken(token: string): void;
  /** Take on the ML-DSA leg this session just enrolled, so the requests
   *  that follow carry both signatures the account now requires. The
   *  caller persists it to the device (`addPersistedCredential`). */
  adoptMlDsaLeg(seed: Uint8Array, epoch: number): void;
  /** Best-effort zero-fill of the in-place signing-key buffer.
   *  Defense-in-depth — a freed `Uint8Array` may still survive
   *  in V8's nursery / old-space between the GC sweeps that
   *  reclaim it. The cryptographer lock-in says we wipe before
   *  the slot drops so a heap snapshot of the post-logout
   *  process can't lift the seed. Idempotent; the action also
   *  drops the credential bundle (returns to `logged_out`). */
  zeroizeSigningKey(): void;
  /** Fired when the
   *  account session is revoked by the server (WS
   *  `session_revoked` frame OR HTTP 401/403 with one of the
   *  four session-revoked i18n keys). Runs the same 6-step
   *  teardown as `logout()` (the user-initiated path) but
   *  short-circuits when the slice is already in
   *  `logged_out` — multiple revoke sources fire
   *  the action concurrently (WS event + 401 fetch + cross-tab
   *  BroadcastChannel) and the mount-once invariant needs an
   *  explicit guard. `reason` flows into
   *  the wire telemetry hook so a future audit lane can
   *  distinguish a new-login displace from an admin force-
   *  logout. */
  markSessionRevoked(reason: string): void;
  /** Halt THIS surface because a newer surface for the same account
   *  claimed this table's singleton slot (HTTP 403 `session.superseded`
   *  — the WS `displaced` frame records the same signal via its reducer
   *  arm). Records `connection.disconnect = surface_displaced` on the
   *  game slice (via the reducer-FREE root store, so this stays out of
   *  the eager cold-login chunk's game reducer graph) AND latches the
   *  take-over notice. Critically NOT a logout: the account session,
   *  cookie, and IDB row are untouched, so the user can take over from
   *  here or continue in the newer surface. */
  markSurfaceDisplaced(reason: string): void;
  /** Take over the table slot for THIS surface after a surface-
   *  displaced halt (the take-over CTA). Clears the take-over notice
   *  latch + restores the game slice's `connection.disconnect` to
   *  `live` and bumps `surface_retry_seq` so the mounted Game effect
   *  re-fires + opens a fresh socket that re-claims the slot. Written
   *  through the reducer-FREE root store so the eager App chunk (which
   *  mounts the modal) need not import the game reducer graph. */
  takeOverSurface(): void;
  /** Refresh the cap-style wallet snapshot from `/accounts/me/sync`.
   *  Called after Lobby mount / table-leave / WS-reconnect / rebuy
   *  so the displayed "지갑 + 진행 중" lines track the BE truth
   *  without rendering a stale free balance after a hold lands.
   *  No-op outside an authenticated (`lobby`/`seated`/`spectating`)
   *  state. */
  setWallet(args: {
    chipFree: number;
    chipInPlay: number;
    chipHoldTotal: number;
  }): void;
  /** Take a seat — enters the `seated` state (derives `mode =
   *  'player'`, `mySeat = seat`). Also the host-auto-seat path. */
  joinTable(args: { tableIdHex: string; mySeat: number }): void;
  /** Attach as a read-only spectator — enters the `spectating` state
   *  (derives `mode = 'spectator'`, `mySeat = null`). Used both for
   *  explicit "관전 모드" entry and for the auto-spectate fallback
   *  when `/join` returns a mid-hand `409 Conflict`. A `seated` →
   *  `spectating` transition additionally wipes the game-slice seed
   *  surface in the SAME atomic root commit (see the action body). */
  joinAsSpectator(args: { tableIdHex: string }): void;
  leaveTable(): void;
  setServerBase(base: string): void;
  logout(): void;
  /** Rehydrate the slice from an encrypted IndexedDB row written
   *  by the previous `login()` (or a sibling reload). Drops the
   *  caller into the `probing` state with the signing key restored
   *  — no password prompt, no `/accounts/login` round-trip. The
   *  `probing` state arms the boot session-validity gate
   *  (`bootProbePending`/derived `phase === "lobby"`). The wallet
   *  snapshot stays unknown until the boot probe / next
   *  `/accounts/me/sync` resolves it. Idempotent — re-entering
   *  against an already-authenticated (or restoring) slice
   *  short-circuits so the in-memory seed buffer is never replaced
   *  / orphaned. */
  restoreSession(args: RestoredSession): void;
  /** Clear the boot session-validity gate so the AuthGate releases
   *  the lobby (transition `probing → lobby`). Called by `main.tsx`
   *  once the boot probe confirms the restored session is live (or
   *  fails TRANSIENTLY — a network / 5xx miss is offline-tolerant
   *  and still mounts the lobby, which retries its reads). A
   *  session-class 401 instead routes through `markSessionRevoked`
   *  → `logged_out`, so this is never called on the revoked path.
   *  Optionally seeds the freshly-probed wallet snapshot
   *  (`walletKnown = true`) so the lobby's first paint shows real
   *  chips instead of the restore-time zeros; without it the lobby
   *  lands `walletKnown = false` (fail-open) and its own reads
   *  retry. */
  clearBootProbe(wallet?: {
    chipFree: number;
    chipInPlay: number;
    chipHoldTotal: number;
  }): void;
}

/**
 * Pure derivation: a `SessionState` → the legacy flat mirror fields
 * every existing consumer reads. The SOLE writer is the transition
 * code, which recomputes these inside the same atomic `set` so no
 * consumer can observe a half-transition. `serverBase` is
 * orthogonal plumbing and carried separately (NOT derived here).
 *
 * `chipFree`/`chipHoldTotal` are orthogonal numeric mirrors gated by
 * `walletKnown`; they are passed in (carried from the wallet snapshot
 * the transition just applied) rather than embedded in the union, and
 * forced to zero whenever there is no authenticated wallet.
 */
function deriveLegacyFields(
  s: SessionState,
  wallet: { chipFree: number; chipInPlay: number; chipHoldTotal: number },
): Omit<AuthState, "sessionState" | "serverBase"> {
  switch (s.kind) {
    case "logged_out":
    case "restoring":
      return {
        phase: "logged_out",
        username: null,
        accountId: null,
        playerIdHex: null,
        signingKey: null,
        ed25519Epoch: 0,
        mlDsaSeed: null,
        mlDsaEpoch: null,
        chipFree: 0,
        chipInPlay: 0,
        chipHoldTotal: 0,
        locale: null,
        tableIdHex: null,
        mySeat: null,
        mode: "player",
        csrfToken: null,
        bootProbePending: false,
        walletKnown: false,
      };
    case "probing":
      return {
        // `probing` derives `phase === "lobby"` + `bootProbePending`
        // so the AuthGate's `phase === "lobby" && bootProbePending`
        // arm fires iff the kind is `probing` — the boot gate.
        phase: "lobby",
        username: s.account.username,
        accountId: s.account.accountId,
        playerIdHex: s.account.playerIdHex,
        signingKey: s.account.signingKey,
        ed25519Epoch: s.account.ed25519Epoch,
        mlDsaSeed: s.account.mlDsaSeed,
        mlDsaEpoch: s.account.mlDsaEpoch,
        // The wallet is structurally unknown during a probe — the
        // restore-time zeros are placeholders, not a real balance.
        chipFree: 0,
        chipInPlay: 0,
        chipHoldTotal: 0,
        locale: s.account.locale,
        tableIdHex: null,
        mySeat: null,
        mode: "player",
        csrfToken: s.account.csrfToken,
        bootProbePending: true,
        walletKnown: false,
      };
    case "lobby":
      return {
        phase: "lobby",
        username: s.account.username,
        accountId: s.account.accountId,
        playerIdHex: s.account.playerIdHex,
        signingKey: s.account.signingKey,
        ed25519Epoch: s.account.ed25519Epoch,
        mlDsaSeed: s.account.mlDsaSeed,
        mlDsaEpoch: s.account.mlDsaEpoch,
        chipFree: wallet.chipFree,
        chipInPlay: wallet.chipInPlay,
        chipHoldTotal: wallet.chipHoldTotal,
        locale: s.account.locale,
        tableIdHex: null,
        mySeat: null,
        mode: "player",
        csrfToken: s.account.csrfToken,
        bootProbePending: false,
        walletKnown: s.walletKnown,
      };
    case "seated":
      return {
        phase: "table",
        username: s.account.username,
        accountId: s.account.accountId,
        playerIdHex: s.account.playerIdHex,
        signingKey: s.account.signingKey,
        ed25519Epoch: s.account.ed25519Epoch,
        mlDsaSeed: s.account.mlDsaSeed,
        mlDsaEpoch: s.account.mlDsaEpoch,
        chipFree: wallet.chipFree,
        chipInPlay: wallet.chipInPlay,
        chipHoldTotal: wallet.chipHoldTotal,
        locale: s.account.locale,
        tableIdHex: s.table.tableIdHex,
        // `mySeat` is non-null by type in the `seated` variant.
        mySeat: s.table.mySeat,
        mode: "player",
        csrfToken: s.account.csrfToken,
        bootProbePending: false,
        walletKnown: s.walletKnown,
      };
    case "spectating":
      return {
        phase: "table",
        username: s.account.username,
        accountId: s.account.accountId,
        playerIdHex: s.account.playerIdHex,
        signingKey: s.account.signingKey,
        ed25519Epoch: s.account.ed25519Epoch,
        mlDsaSeed: s.account.mlDsaSeed,
        mlDsaEpoch: s.account.mlDsaEpoch,
        chipFree: wallet.chipFree,
        chipInPlay: wallet.chipInPlay,
        chipHoldTotal: wallet.chipHoldTotal,
        locale: s.account.locale,
        tableIdHex: s.table.tableIdHex,
        // The spectating variant has no seat field — `mySeat` derives
        // to `null`, byte-identical to the prior `lane: 'spectator'`
        // membership whose `mySeat` was null by convention.
        mySeat: null,
        mode: "spectator",
        csrfToken: s.account.csrfToken,
        bootProbePending: false,
        walletKnown: s.walletKnown,
      };
  }
}

/**
 * Materialise the full flat `AuthState` from a `SessionState` + the
 * orthogonal carry (serverBase + the wallet mirror). The transition
 * actions call this inside ONE `set` so `sessionState` and every
 * derived mirror always commit together (atomic-set invariant).
 */
function project(
  sessionState: SessionState,
  carry: {
    serverBase: string;
    chipFree: number;
    chipInPlay: number;
    chipHoldTotal: number;
  },
): AuthState {
  return {
    sessionState,
    serverBase: carry.serverBase,
    ...deriveLegacyFields(sessionState, carry),
  };
}

function initialAuth(): AuthState {
  // A brand-new tab boots in `logged_out`; the derived mirrors paint
  // the LoginScreen until `login()` / `restoreSession()` fires.
  return project(
    { kind: "logged_out" },
    { serverBase: "", chipFree: 0, chipInPlay: 0, chipHoldTotal: 0 },
  );
}

/**
 * The auth/session lifecycle slice — `AuthState` mirror + the
 * transition actions (login / restore / probe-clear / wallet / table-
 * membership / logout). Composed into the unified root store by
 * `store/root.ts`.
 *
 * This is a LITERAL extraction of the prior `create()` body into a
 * slice factory — every action is byte-identical; only the `create`
 * wrapper moved out. `set` / `get` are SCOPED to the `auth` sub-slice
 * (root.ts builds them); each transition calls `project()` inside ONE
 * `set` so `sessionState` + every derived mirror commit atomically,
 * and the returned `AuthState` shallow-merges over the auth sub-state,
 * leaving the game sub-slice untouched. The `useAuthStore` export
 * below is a selector-bound VIEW over the `auth` sub-slice preserving
 * the exact `getState()` / hook-with-selector surface every consumer +
 * `auth_headers` + `runLogoutSequence` depend on.
 */
export type AuthSlice = AuthState & AuthActions;

/**
 * Replace the credential bundle of an authenticated session, keeping the
 * session in whatever state it is in. No identity ⇒ nothing to change;
 * an unchanged bundle ⇒ the same state object, so subscribers do not
 * re-render.
 */
function withAccount(
  state: AuthSlice,
  update: (account: AuthIdentity) => AuthIdentity,
): AuthSlice {
  const s = state.sessionState;
  if (
    s.kind !== "probing" &&
    s.kind !== "lobby" &&
    s.kind !== "seated" &&
    s.kind !== "spectating"
  ) {
    return state;
  }
  const account = update(s.account);
  if (account === s.account) return state;
  const next: SessionState =
    s.kind === "probing"
      ? { kind: "probing", account }
      : s.kind === "lobby"
        ? { kind: "lobby", account, walletKnown: s.walletKnown }
        : s.kind === "seated"
          ? {
              kind: "seated",
              account,
              walletKnown: s.walletKnown,
              table: s.table,
            }
          : {
              kind: "spectating",
              account,
              walletKnown: s.walletKnown,
              table: s.table,
            };
  return {
    ...state,
    ...project(next, {
      serverBase: state.serverBase,
      chipFree: state.chipFree,
      chipInPlay: state.chipInPlay,
      chipHoldTotal: state.chipHoldTotal,
    }),
  };
}

export function buildAuthSlice(
  set: SliceSet<AuthSlice>,
  _get: () => AuthSlice,
): AuthSlice {
  void _get;
  return {
    ...initialAuth(),

    login(args) {
      const seed = hexToBytes(args.signingKeyHex);
      if (seed.length !== 32) {
        throw new Error("auth.login: signing key must be 32 bytes");
      }
      const mlDsaSeed =
        args.mlDsa != null ? hexToBytes(args.mlDsa.seedHex) : null;
      if (mlDsaSeed !== null && mlDsaSeed.length !== 32) {
        throw new Error("auth.login: ml-dsa seed must be 32 bytes");
      }
      const mlDsaEpoch = args.mlDsa?.epoch ?? null;
      set((state) => {
        const account: AuthIdentity = {
          username: args.username,
          accountId: args.accountId,
          playerIdHex: args.playerIdHex,
          signingKey: seed,
          // Login returns the GENESIS ed25519 seed (epoch 0) and, for an
          // account that enrolled one, the ML-DSA seed the server keeps
          // sealed under the same password — so both legs arrive together
          // on any device.
          ed25519Epoch: 0,
          mlDsaSeed,
          mlDsaEpoch,
          csrfToken: args.csrfToken,
          locale: args.locale ?? null,
        };
        // Fresh login mints a brand-new session cookie — there is
        // nothing stale to probe, so we land directly in `lobby` with the
        // wallet known (the login response carried the real balance).
        return project(
          { kind: "lobby", account, walletKnown: true },
          {
            serverBase: state.serverBase,
            chipFree: args.chipFree,
            chipInPlay: args.chipInPlay,
            chipHoldTotal: args.chipHoldTotal,
          },
        );
      });
      // A fresh login is the sole non-CTA clear point for the notice
      // latches (revoke / surface-displaced): the user re-authenticated,
      // so any standing "logged in elsewhere" / "another surface took
      // over" notice from the prior session is resolved. Teardown paths
      // (logout / leaveTable / reset) deliberately do NOT clear these —
      // only this re-auth + the modal CTAs do.
      useUIStore.getState().clearPendingNotices();
      // Cross-tab login sync.
      // Fan the login + csrf-token rotation to every sibling tab
      // so any stale "logged out" surface (LoginScreen mounted in
      // a prior tab) re-renders against the fresh slice on the
      // next storage / BroadcastChannel tick. The receiving tab
      // hard-reloads via `window.location.reload()` because the
      // signing-key buffer cannot cross the tab boundary safely
      // (cryptographer lock-in — the in-memory wipe on logout
      // owns the seed lifetime).
      publishCrossTab({
        type: "login_succeeded",
        playerIdHex: args.playerIdHex,
      });
      publishCrossTab({
        type: "session_token_issued",
        csrfToken: args.csrfToken,
      });
      // Encrypt the boot-rehydrate payload into IndexedDB so a PWA
      // update reload (or any plain refresh) lands the user back
      // in the lobby without the password prompt. The wrap key is
      // a non-extractable AES-GCM CryptoKey — raw seed bytes never
      // touch disk. Fire-and-forget; a private-mode / IDB-blocked
      // platform falls back to the in-memory-only model.
      void persistSession({
        signingKey: seed,
        mlDsaSeed,
        mlDsaEpoch,
        username: args.username,
        accountId: args.accountId,
        playerIdHex: args.playerIdHex,
        locale: args.locale ?? null,
        csrfToken: args.csrfToken,
      });
    },

    restoreSession(args) {
      if (args.signingKey.length !== 32) {
        throw new Error("auth.restoreSession: signing key must be 32 bytes");
      }
      set((state) => {
        // Idempotency — a second rehydrate against an already-
        // authenticated (or in-flight restoring) slice (sibling tab BC
        // echo + boot path converging on the same IDB row) would
        // otherwise replace the in-memory seed buffer and orphan the
        // prior copy (cryptographer seed-lifetime lock-in). Only
        // transition out of `logged_out` (`restoring` is a transient
        // boot sub-state that this same call replaces — the boot path
        // never re-enters mid-restore, but guarding both keeps the
        // single-entry invariant explicit).
        if (state.sessionState.kind !== "logged_out") return state;
        const account: AuthIdentity = {
          username: args.username,
          accountId: args.accountId,
          playerIdHex: args.playerIdHex,
          signingKey: args.signingKey,
          ed25519Epoch: args.ed25519Epoch,
          mlDsaSeed: args.mlDsaSeed,
          mlDsaEpoch: args.mlDsaEpoch,
          csrfToken: args.csrfToken,
          locale: args.locale,
        };
        // Enter `probing` — arms the boot session-validity gate
        // (derived `phase === "lobby"` + `bootProbePending`). The
        // restored cookie may already be swept (idle/absolute TTL); the
        // AuthGate holds the lobby behind the neutral loading shell
        // until `main.tsx`'s probe resolves it via `clearBootProbe`
        // (live / transient) or `markSessionRevoked` (session 401), so a
        // revoked session never flashes the alarming "예약된 좌석" banner
        // + "$0". The wallet is structurally unknown in `probing`.
        return project(
          { kind: "probing", account },
          {
            serverBase: state.serverBase,
            chipFree: 0,
            chipInPlay: 0,
            chipHoldTotal: 0,
          },
        );
      });
    },

    clearBootProbe(wallet) {
      set((state) => {
        const s = state.sessionState;
        // The gate is only armed in `probing`. Anywhere else this is a
        // no-op (preserves the old short-circuit semantics: a fresh
        // `login()` already cleared the gate, a revoked probe already
        // logged out).
        if (s.kind !== "probing") return state;
        // probe-200 → seed the freshly-probed wallet (walletKnown=true);
        // probe-transient-miss → fail OPEN to lobby with the wallet still
        // unknown so the lobby's own reads retry. Either way: probing →
        // lobby. NEVER probing → logged_out (that path is owned solely by
        // the authedRequest 401 cascade calling markSessionRevoked).
        if (wallet !== undefined) {
          return project(
            { kind: "lobby", account: s.account, walletKnown: true },
            {
              serverBase: state.serverBase,
              chipFree: wallet.chipFree,
              chipInPlay: wallet.chipInPlay,
              chipHoldTotal: wallet.chipHoldTotal,
            },
          );
        }
        return project(
          { kind: "lobby", account: s.account, walletKnown: false },
          {
            serverBase: state.serverBase,
            chipFree: 0,
            chipInPlay: 0,
            chipHoldTotal: 0,
          },
        );
      });
    },

    setCsrfToken(token) {
      set((state) =>
        withAccount(state, (account) =>
          account.csrfToken === token
            ? account
            : { ...account, csrfToken: token },
        ),
      );
    },

    adoptMlDsaLeg(seed, epoch) {
      set((state) =>
        withAccount(state, (account) => ({
          ...account,
          mlDsaSeed: seed,
          mlDsaEpoch: epoch,
        })),
      );
    },

    setWallet(args) {
      set((state) => {
        const s = state.sessionState;
        // A real `/accounts/me/sync` snapshot only applies to an
        // authenticated, non-probing state (`probing` has no wallet by
        // construction; the boot probe seeds the wallet via
        // `clearBootProbe`, not here).
        if (
          s.kind !== "lobby" &&
          s.kind !== "seated" &&
          s.kind !== "spectating"
        ) {
          return state;
        }
        if (
          s.walletKnown &&
          state.chipFree === args.chipFree &&
          state.chipHoldTotal === args.chipHoldTotal &&
          state.chipInPlay === args.chipInPlay
        ) {
          return state;
        }
        // The wallet is now known even if the value happens to equal the
        // placeholder zeros.
        const next: SessionState =
          s.kind === "lobby"
            ? { kind: "lobby", account: s.account, walletKnown: true }
            : s.kind === "seated"
              ? {
                  kind: "seated",
                  account: s.account,
                  walletKnown: true,
                  table: s.table,
                }
              : {
                  kind: "spectating",
                  account: s.account,
                  walletKnown: true,
                  table: s.table,
                };
        return project(next, {
          serverBase: state.serverBase,
          chipFree: args.chipFree,
          chipInPlay: args.chipInPlay,
          chipHoldTotal: args.chipHoldTotal,
        });
      });
    },

    joinTable(args) {
      set((state) => {
        const s = state.sessionState;
        // A seat can only be taken from an authenticated state (lobby,
        // a prior seat, or while spectating). Defensive no-op otherwise.
        if (
          s.kind !== "lobby" &&
          s.kind !== "seated" &&
          s.kind !== "spectating"
        ) {
          return state;
        }
        // Enter the `seated` variant — `mySeat` is non-null by type, so
        // "seated with no seat" is structurally unrepresentable. The
        // KIND is the lane (derives `mode = 'player'`).
        return project(
          {
            kind: "seated",
            account: s.account,
            walletKnown: s.walletKnown,
            table: {
              tableIdHex: args.tableIdHex,
              mySeat: args.mySeat,
            },
          },
          {
            serverBase: state.serverBase,
            chipFree: state.chipFree,
            chipInPlay: state.chipInPlay,
            chipHoldTotal: state.chipHoldTotal,
          },
        );
      });
    },

    joinAsSpectator(args) {
      // A `seated` → `spectating` flip (mid-hand auto-spectate fallback
      // when `/join` 409s, or a manual switch) must drop the
      // seed-bearing surface the player lane accumulated, so a stale
      // `final_seed` / `my_hole_cards` / `dealt_seats` can never be
      // re-derived on the read-only lane. This is the PRIMARY wipe,
      // co-located with the lane transition itself — the reducer-level
      // `isSpectator` strip in `hand_lifecycle` stays as a
      // defense-in-depth backstop for a mis-routed player-lane frame.
      //
      // The seed surface lives in the GAME slice, not here, so this
      // path drives BOTH sub-slices through ONE root `setState` —
      // committing the auth lane flip and the game seed wipe in a
      // single atomic root mutation (no half-transition is observable).
      // `useRootStore` is the reducer-FREE root module (no game-reducer
      // import), so this stays out of the eager cold-login chunk
      // (de-eager); the wipe is a plain field overwrite, not a
      // reducer call.
      const fromSeated =
        useRootStore.getState().auth.sessionState.kind === "seated";
      if (fromSeated) {
        useRootStore.setState((root) => {
          const a = root.auth;
          const s = a.sessionState;
          // Re-narrow under the root snapshot; only `seated` reaches
          // here, but guard so a racing transition can't mis-fire.
          if (s.kind !== "seated") return {};
          const nextAuth = project(
            {
              kind: "spectating",
              account: s.account,
              walletKnown: s.walletKnown,
              table: { tableIdHex: args.tableIdHex },
            },
            {
              serverBase: a.serverBase,
              chipFree: a.chipFree,
              chipInPlay: a.chipInPlay,
              chipHoldTotal: a.chipHoldTotal,
            },
          );
          return {
            auth: { ...a, ...nextAuth },
            game: {
              ...root.game,
              my_hole_cards: null,
              dealt_seats: [],
            },
          };
        });
        return;
      }
      // lobby → spectating (or an idempotent spectating → spectating
      // re-entry): no seated seed surface to wipe, so the pure auth
      // transition through the scoped `set` is sufficient.
      set((state) => {
        const s = state.sessionState;
        if (s.kind !== "lobby" && s.kind !== "spectating") return state;
        return project(
          {
            kind: "spectating",
            account: s.account,
            walletKnown: s.walletKnown,
            table: { tableIdHex: args.tableIdHex },
          },
          {
            serverBase: state.serverBase,
            chipFree: state.chipFree,
            chipInPlay: state.chipInPlay,
            chipHoldTotal: state.chipHoldTotal,
          },
        );
      });
    },

    leaveTable() {
      // Leaving a table returns the (still-authenticated) caller to the
      // lobby AND drains the felt — the auth-lane flip (`seated` /
      // `spectating` → `lobby`, carrying `walletKnown` forward) and the
      // table-slice wipe commit in ONE atomic root `set`.
      // Driving the drain off this transition (not a `reset()` effect
      // riding the mounted Game component) means the felt is gone the
      // instant the lobby lane lands, with no half-transition window.
      // Outside a table this is a no-op (logout is the distinct teardown
      // path). Follows the `joinAsSpectator` cross-slice pattern:
      // `useRootStore.setState` drives BOTH sub-slices through one root
      // mutation, and `useRootStore` stays reducer-FREE so this never
      // drags the game reducer graph into the eager cold-login chunk.
      useRootStore.setState((root) => {
        const a = root.auth;
        const s = a.sessionState;
        if (s.kind !== "seated" && s.kind !== "spectating") return {};
        const nextAuth = project(
          { kind: "lobby", account: s.account, walletKnown: s.walletKnown },
          {
            serverBase: a.serverBase,
            chipFree: a.chipFree,
            chipInPlay: a.chipInPlay,
            chipHoldTotal: a.chipHoldTotal,
          },
        );
        return {
          auth: { ...a, ...nextAuth },
          game: resetTableSlice(root.game),
        };
      });
      // The notice latches are NOT cleared here — a table leave is a
      // teardown, and the notices must outlive it (they clear only on
      // the modal CTA / a fresh `login()`). The freshly-rebuilt game
      // slice already carries `disconnect: live`.
    },

    setServerBase(base) {
      // `serverBase` is orthogonal plumbing carried outside the session
      // discriminant — it survives every transition and never affects the
      // lifecycle. Re-project the current `sessionState` so the single
      // source of truth stays canonical.
      set((state) =>
        project(state.sessionState, {
          serverBase: base,
          chipFree: state.chipFree,
          chipInPlay: state.chipInPlay,
          chipHoldTotal: state.chipHoldTotal,
        }),
      );
    },

    zeroizeSigningKey() {
      // Defense-in-depth wipe — fill the in-place buffers AND drop the
      // credential bundle, for BOTH the ed25519 seed and the ml-dsa seed
      // (a hybrid account holds both; the ml-dsa seed must not survive in
      // the heap). Idempotent: no credentials ⇒ no-op.
      set((state) => {
        const s = state.sessionState;
        if (
          s.kind !== "probing" &&
          s.kind !== "lobby" &&
          s.kind !== "seated" &&
          s.kind !== "spectating"
        ) {
          return state;
        }
        if (s.account.signingKey !== null) s.account.signingKey.fill(0);
        if (s.account.mlDsaSeed !== null) s.account.mlDsaSeed.fill(0);
        // Dropping the bundle returns to `logged_out` (the derived
        // `signingKey`/`mlDsaSeed`/`csrfToken` all become null).
        return project(
          { kind: "logged_out" },
          {
            serverBase: state.serverBase,
            chipFree: 0,
            chipInPlay: 0,
            chipHoldTotal: 0,
          },
        );
      });
    },

    logout() {
      // User-initiated logout — same 6-step orchestration as
      // `markSessionRevoked` (the server-driven revoke path).
      // Delegating through the shared helper keeps the two
      // entry points byte-identical so a future revoke-reason
      // distinction stays additive.
      runLogoutSequence(LOGOUT_REASON_USER_INITIATED);
    },

    markSessionRevoked(reason) {
      runLogoutSequence(reason);
    },

    markSurfaceDisplaced(reason) {
      // Surface-halt — NOT a logout. Record the surface-displaced
      // signal on the game slice through the reducer-FREE root store
      // (a plain field overwrite, never a game reducer call) so this
      // never drags the game reducer graph into the eager cold-login
      // chunk — the same constraint `runLogoutSequence`'s felt drain
      // honours. The mounted Game connection effect subscribes to
      // `connection.disconnect.kind`; the flip off `live` re-runs it,
      // closing the WS via its cleanup and refusing to reopen until
      // take-over restores `live`. Background polling (seed safety-net /
      // wallet_sync) is NOT gated here — its next signed request earns
      // the same 403 `session.superseded` and re-fires this action,
      // which is an idempotent no-op (same-value latch + same signal),
      // so it self-quiesces without a runaway loop. Latch the take-over
      // notice so the App-level gate mounts the modal regardless of
      // whether a Game surface is mounted (the HTTP 403 path can fire
      // from the Lobby too).
      useRootStore.setState((root) => ({
        game: {
          ...root.game,
          connection: {
            ...root.game.connection,
            disconnect: { kind: "surface_displaced", reason },
          },
        },
      }));
      useUIStore.getState().setPendingSurfaceNotice(reason);
    },

    takeOverSurface() {
      // Clear the take-over notice latch (the modal dismisses) + reopen
      // this surface's connection: restore `disconnect` to `live` and
      // bump `surface_retry_seq`. With a mounted Game surface this
      // re-fires its connection effect, which reopens the socket whose
      // handshake re-claims the singleton slot. From the LOBBY (the HTTP
      // 403 `session.superseded` can fire there too) there is no
      // persistent WS to reopen — the `surface_retry_seq` bump has no
      // listener; restoring `live` simply lifts the on-close reconnect
      // suppressor, and the next signed Lobby request re-claims the slot
      // by itself. Written through the reducer-FREE root store (a plain
      // field overwrite, not a game reducer call) so the eager App chunk
      // stays free of the game reducer graph.
      useUIStore.getState().clearPendingNotices();
      useRootStore.setState((root) => ({
        game: {
          ...root.game,
          connection: { ...root.game.connection, disconnect: { kind: "live" } },
          surface_retry_seq: root.game.surface_retry_seq + 1,
        },
      }));
    },
  };
}

/**
 * `useAuthStore` — a selector-bound VIEW over the `auth` sub-slice of
 * the unified root store. Consumers,
 * `auth_headers`, `runLogoutSequence`, `main.tsx`'s boot probe, and the
 * unit tests keep importing this hook and using it byte-identically:
 *
 *   - `useAuthStore((s) => s.field)` — the selector runs against the
 *     `auth` sub-slice (every auth field), so it reads exactly what it
 *     did against the standalone store.
 *   - `useAuthStore.getState()` — returns the `auth` sub-slice. Every
 *     auth field + action is present. `runLogoutSequence`'s
 *     `getState().sessionState` / `.serverBase` reads resolve here.
 *   - `useAuthStore.setState(...)` — writes the `auth` sub-slice.
 *   - `useAuthStore.subscribe(...)` — forwards to the root store (no
 *     auth consumer uses it today; preserved for surface parity).
 *
 * `useRootStore` is referenced ONLY inside these closures (never at
 * module-eval time): the store is composed on first access, after the
 * slices have registered themselves.
 */
function useAuthStoreHook<T>(selector: (state: AuthSlice) => T): T {
  return useRootStore((root) => selector(root.auth));
}

export interface AuthStoreView {
  <T>(selector: (state: AuthSlice) => T): T;
  getState(): AuthSlice;
  setState: SliceSet<AuthSlice>;
  subscribe(listener: () => void): () => void;
}

export const useAuthStore: AuthStoreView = Object.assign(useAuthStoreHook, {
  getState: (): AuthSlice => useRootStore.getState().auth,
  setState: ((partial: unknown, replace?: boolean): void => {
    useRootStore.setState((root) => {
      const update =
        typeof partial === "function"
          ? (partial as (s: AuthSlice) => AuthSlice | Partial<AuthSlice>)(
              root.auth,
            )
          : (partial as AuthSlice | Partial<AuthSlice>);
      void replace;
      return { auth: { ...root.auth, ...update } };
    });
  }) as SliceSet<AuthSlice>,
  subscribe: (listener: () => void): (() => void) =>
    useRootStore.subscribe(listener),
});

/**
 * Shared 6-step logout orchestration. Routes
 * both the user-initiated `logout()` action and the server-
 * driven `markSessionRevoked(reason)` action through the same
 * teardown so the WS reconnect-loop abort, server-side cookie
 * revoke, key zeroization, and slice reset always run in the
 * exact ordering the spec pins:
 *
 *   step 0  short-circuit when the slice is already
 *           `logged_out` — the multi-source race
 *           against WS `session_revoked` + HTTP 401 + cross-tab
 *           BroadcastChannel converges on a single mount-once
 *           invariant; without the guard three POSTs + three
 *           Zustand sets would fan out). Now a trivial kind check
 *           on the single source of truth.
 *   step 1  POST `/accounts/logout` — fire-and-forget. The BE
 *           is idempotent (an absent / stale cookie returns
 *           200 with the clearing `Set-Cookie`).
 *   step 2  zeroize the signing-key buffer in place — best-
 *           effort GC defense (cryptographer lock-in).
 *   steps 3+4  ONE atomic root `set` enters
 *           `logged_out` AND drains the per-table game slice
 *           together — the AuthGate falls through to LoginScreen
 *           on the next render (the derived `phase` flips with
 *           it) and the felt is gone the same frame, no longer
 *           dependent on a separate App-level subscriber reading
 *           the flipped phase.
 *   step 5  WS client teardown rides the Game-route
 *           `useEffect([signingKey, ...])` cleanup: the auth-lane
 *           flip to `logged_out` clears `signingKey` + unmounts
 *           the Game route, firing the effect's `handle.close()`.
 *           The on-close handler also reads `connection.
 *           displaced`, which the WS-borne revoke path sets
 *           before the close event fires — both converge on the
 *           same outcome (no reconnect).
 *   step 6  AuthGate phase branch mounts LoginScreen +
 *           DisplacedModal overlay (when the displaced flag
 *           sits on the game-store connection slot). The
 *           modal countdown drives the final user-visible
 *           cleanup CTA.
 *
 * `reason` flows into a future telemetry surface so an
 * operator audit can correlate a logout with its trigger
 * (`new_login` / `explicit_logout` / `admin_force` /
 * `user_initiated`). The current implementation is a no-op on
 * reason — the wire telemetry hook lands in a follow-up.
 */
/** The reason a user-initiated `logout()` carries, as opposed to any
 *  server-driven revoke (a fresh login elsewhere, an admin force-out,
 *  an HTTP 401). Three places read it and it used to be a bare literal
 *  in each — which is how the notice branch and the cross-tab branch
 *  came to disagree about what the same word meant. */
export const LOGOUT_REASON_USER_INITIATED = "user_initiated";

function runLogoutSequence(reason: string): void {
  // step 0 — idempotency guard. A second entry against an
  // already-wiped slice short-circuits before the POST so the
  // mount-once invariant holds across the multi-source race.
  if (useAuthStore.getState().sessionState.kind === "logged_out") {
    return;
  }
  // step 0b — latch the session-revoke notice (the standing
  // obligation to TELL the user). This is an INDEPENDENT one-way
  // latch on the UI slice, set BEFORE the teardown below. The
  // teardown must never clear it — that was the one-render-flash root
  // (the modal mount-gate used to ride the same flag the teardown
  // wiped in the same tick). The latch clears only on the modal CTA /
  // countdown completion or the next successful `login()`.
  //
  // The obligation only exists when the user does NOT already know.
  // This routine is the one path for both a server revoke and the
  // user's own logout, and it used to latch for both — so pressing
  // 로그아웃 answered with "이 계정이 다른 기기 또는 브라우저에서
  // 로그인됐어요", which is not merely noise: it is false, and it says
  // someone else is in their account. Nothing to tell someone about a
  // thing they just did; the teardown lands them on the login screen,
  // which is the whole of what they asked for.
  if (reason !== LOGOUT_REASON_USER_INITIATED) {
    useUIStore.getState().setPendingRevokeNotice(reason);
  }
  // step 1 — best-effort server-side revoke.
  const serverBase = useAuthStore.getState().serverBase;
  void fetch(`${serverBase}/accounts/logout`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: "{}",
  }).catch(() => {
    // Network failure — local wipe still runs (fail-closed).
  });
  // step 1b — clear the encrypted IndexedDB row so a revoked
  // session can never rehydrate on the next boot. Fire-and-
  // forget; the row is also dropped on the next successful
  // login if this clear races a reload.
  void clearPersistedSession();
  // steps 2 + 3 + 4 — zeroize BOTH seeds in place (ed25519 + ml-dsa),
  // then a SINGLE atomic root `set` that flips the auth lane to
  // `logged_out` AND drains the felt together. The
  // invariant cannot be observed mid-transition; `sessionState` + every
  // derived auth mirror + the wiped game sub-slice all commit in the
  // same root mutation, so the felt is gone the instant the auth lane
  // flips — no longer dependent on a `reset()` effect riding the
  // mounted Game component. The ml-dsa seed of a hybrid account must be
  // wiped here too or it survives logout in the heap. `useRootStore`
  // stays reducer-FREE (the `resetTableSlice` drain is a plain field
  // overwrite, NOT a reducer call), so this never drags the game
  // reducer graph into the eager cold-login chunk — same constraint the
  // `joinAsSpectator` cross-slice wipe honours.
  useRootStore.setState((root) => {
    const a = root.auth;
    const s = a.sessionState;
    if (
      s.kind === "probing" ||
      s.kind === "lobby" ||
      s.kind === "seated" ||
      s.kind === "spectating"
    ) {
      if (s.account.signingKey !== null) s.account.signingKey.fill(0);
      if (s.account.mlDsaSeed !== null) s.account.mlDsaSeed.fill(0);
    }
    const nextAuth = project(
      { kind: "logged_out" },
      {
        serverBase: a.serverBase,
        chipFree: 0,
        chipInPlay: 0,
        chipHoldTotal: 0,
      },
    );
    return {
      auth: { ...a, ...nextAuth },
      game: resetTableSlice(root.game),
    };
  });
  // The revoke notice latch (set in step 0b above) is intentionally
  // LEFT ALONE here — it must outlive this teardown so the modal keeps
  // dwelling (the prior code cleared it in the same tick, flashing the
  // modal for a single render). It clears only on the modal CTA /
  // countdown or the next `login()`.
  // step 5-6 — WS client teardown + LoginScreen swap fire on the auth
  // lane flip to `logged_out` (the AuthGate phase branch + the
  // App-level WS-cleanup effect both read it). Keeping the felt drain
  // out of a synchronous game-store import here is what avoids closing
  // a cycle through the WS client — the drain rides `useRootStore`
  // (reducer-free), not `store/game/store.ts`.
  //
  // Cross-tab lifecycle fan-out. Fan
  // the lifecycle event to every sibling tab so they converge
  // on the logged-out surface. `reason` discriminates the
  // event type: a user-initiated `logout()` carries the
  // sentinel reason and routes through `logout_explicit`; any
  // server-driven revoke (WS event, HTTP 401, or the cross-tab
  // BC echo path itself) routes through `session_revoked` so
  // the receiving tab can surface the DisplacedModal
  // automatically. The receiving tab's
  // `markSessionRevoked()` action is idempotent (the
  // already-`logged_out` guard short-circuits re-entry), so a tab that
  // already torn itself down before the broadcast arrived
  // ignores the echo cleanly.
  if (reason === LOGOUT_REASON_USER_INITIATED) {
    publishCrossTab({ type: "logout_explicit", reason: reason });
  } else {
    publishCrossTab({ type: "session_revoked", reason: reason });
  }
}

// The root store composes this slice; it asks for it here rather than
// importing it, so the two modules do not import each other.
registerAuthSlice(buildAuthSlice);
